import * as THREE from 'three';
import { MMLCube } from './MMLParser';
import { animatedParcelTracker } from './AnimatedParcelTracker';
import parcelCollisionConfigs, { generateAllParcelConfigs } from './ParcelCollisionConfigs';
import defaultCastleConfig from './CastleCollisionConfig';

export interface Barrier {
  id: string;  // ID da parcela para rastreamento
  position: {
    x: number;
    y: number;
    z: number;
  };
  size: {
    width: number;
    height: number;
    depth: number;
  };
  isAnimated: boolean;  // Indica se a parcela é animada
}

export class MMLCollisionSystem {
  private barriers: Barrier[] = [];
  private worldBounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  };

  constructor() {
    this.worldBounds = {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
      minZ: 0,
      maxZ: 0
    };

    // Inicializar a barreira do castelo com a configuração padrão
    // Isso garante que o castelo tenha uma caixa de colisão mesmo antes de ser carregado visualmente
    this.setBarrier(defaultCastleConfig);
  }

  setBarriersFromMML(cubes: MMLCube[]): void {
    this.barriers = [];

    // Calculate world bounds
    if (cubes.length > 0) {
      this.worldBounds = {
        minX: cubes[0].x,
        maxX: cubes[0].x + cubes[0].width,
        minY: cubes[0].y,
        maxY: cubes[0].y + cubes[0].height,
        minZ: cubes[0].z,
        maxZ: cubes[0].z + cubes[0].depth
      };
    } else {
      return; // Retorna se não houver cubos para evitar erro de tipo
    }

    cubes.forEach(cube => {
      // Update world bounds
      this.worldBounds.minX = Math.min(this.worldBounds.minX, cube.x);
      this.worldBounds.maxX = Math.max(this.worldBounds.maxX, cube.x + cube.width);
      this.worldBounds.minY = Math.min(this.worldBounds.minY, cube.y);
      this.worldBounds.maxY = Math.max(this.worldBounds.maxY, cube.y + cube.height);
      this.worldBounds.minZ = Math.min(this.worldBounds.minZ, cube.z);
      this.worldBounds.maxZ = Math.max(this.worldBounds.maxZ, cube.z + cube.depth);

      // Verificar se a parcela é animada
      const isAnimated = !!(cube.animations && cube.animations.length > 0);

      // Criar barreira com a mesma posição e dimensões da parcel visual
      // Isso garante que a colisão corresponda exatamente ao formato visual de cada parcel
      const parcelId = cube.id;

      // Ajustar a posição para alinhar com as parcelas visuais
      // Com o fator de escala de 4.0, precisamos garantir que as caixas de colisão
      // estejam exatamente alinhadas com as parcelas visuais

      // Não precisamos mais de offsets fixos, pois vamos usar a posição exata da parcela
      // O MMLParser já aplica o fator de escala e o espaçamento correto

      // Apenas para debug
      if (parcelId.includes('parcel-0')) {
        console.log('Parcel 0 - Posição original:', { x: cube.x, y: cube.y, z: cube.z });
      }

      // Usar as configurações de colisão do arquivo ParcelCollisionConfigs.ts
      // Isso permite editar as configurações de forma organizada em um arquivo separado
      // Gerar configurações para todas as parcelas de 0 a 281
      const parcelConfigs = generateAllParcelConfigs();

      // Extrair o número da parcel do ID
      const parcelNumber = parcelId.replace('parcel-', '').split('-')[0];

      // Obter a configuração específica para esta parcel ou usar a configuração padrão
      const config = parcelConfigs[parcelNumber] || parcelConfigs['default'];

      // Aplicar os offsets e escalas da configuração
      const finalOffsetX = config.offsetX;
      const finalOffsetZ = config.offsetZ;
      const scaleWidth = config.scaleWidth;
      const scaleHeight = config.scaleHeight;
      const scaleDepth = config.scaleDepth;

      // Log para debug de parcelas importantes
      if (parcelNumber === '0' || parcelNumber === '1' || parcelNumber === '279') {
        console.log(`Aplicando configuração para parcel ${parcelNumber}:`, config);
      }

      // Criar barreira com a posição ajustada e dimensões da parcela visual
      this.barriers.push({
        id: parcelId,  // Armazenar o ID da parcela
        position: {
          x: cube.x + finalOffsetX, // Aplicar o offset horizontal
          y: cube.y,
          z: cube.z + finalOffsetZ  // Aplicar o offset de profundidade
        },
        size: {
          width: cube.width * scaleWidth,
          height: cube.height * scaleHeight,
          depth: cube.depth * scaleDepth
        },
        isAnimated: isAnimated  // Indicar se a parcela é animada
      });

      // Log para debug de parcelas importantes
      if (parcelId.includes('parcel-0') || parcelId.includes('parcel-1') || parcelId.includes('parcel-279')) {
        console.log(`Barreira criada para ${parcelId}:`, {
          posicaoOriginal: { x: cube.x, y: cube.y, z: cube.z },
          posicaoAjustada: { x: cube.x + finalOffsetX, y: cube.y, z: cube.z + finalOffsetZ },
          offsets: { x: finalOffsetX, z: finalOffsetZ },
          tamanhoOriginal: { width: cube.width, height: cube.height, depth: cube.depth },
          tamanhoAjustado: {
            width: cube.width * scaleWidth,
            height: cube.height * scaleHeight,
            depth: cube.depth * scaleDepth
          },
          escalas: { width: scaleWidth, height: scaleHeight, depth: scaleDepth }
        });
      }

      // Log para debug de parcels específicas
      if (parcelId.includes('parcel-0')) {
        console.log(`Parcel 0 position:`, { x: cube.x, y: cube.y, z: cube.z, width: cube.width, height: cube.height, depth: cube.depth });
      }
    });

    // Log para debug - verificar o número total de barreiras criadas
    console.log(`Total de barreiras criadas: ${this.barriers.length}`);

    // Log para debug - verificar os limites do mundo
    console.log('Limites do mundo:', this.worldBounds);

    // Log para debug - verificar a parcel 279
    const parcel279 = this.barriers.find(barrier => barrier.id === 'parcel-279-size-8');
    if (parcel279) {
      console.log('Parcel 279 dimensions:', {
        position: parcel279.position,
        size: parcel279.size
      });
    }
  }

  checkCollision(position: THREE.Vector3): boolean {
    // Usar uma margem proporcional ao tamanho das parcelas (4x maior que antes)
    const margin = 0.2; // Margem para detecção de colisão (aumentada de 0.05 para 0.2)

    // Removido o limite de altura máxima para permitir voo livre ao redor da lua
    // Removido também as verificações de limites do mundo para permitir voo em qualquer lugar
    // A nave agora pode voar para qualquer lugar, inclusive ao redor da lua

    // Check collision with barriers - verificando colisão com cada parcel
    for (const barrier of this.barriers) {
      // Obter a altura real da parcela
      let effectiveHeight = barrier.size.height;

      // Para parcelas animadas, verificar a altura atual no rastreador
      if (barrier.isAnimated) {
        // Definir a parcela atual no rastreador
        animatedParcelTracker.setCurrentParcel(barrier.id);

        // Obter a altura atual da parcela animada, se disponível
        const currentHeight = animatedParcelTracker.getCurrentParcelHeight();
        if (currentHeight !== null) {
          effectiveHeight = currentHeight - barrier.position.y;
        }
      }

      // Calcular os limites exatos da parcela
      const minX = barrier.position.x;
      const maxX = barrier.position.x + barrier.size.width;
      const minY = barrier.position.y;
      const maxY = barrier.position.y + effectiveHeight;
      const minZ = barrier.position.z;
      const maxZ = barrier.position.z + barrier.size.depth;

      // Verificar se a posição está dentro dos limites horizontais da parcela
      const isWithinHorizontalBounds =
        position.x >= minX - margin && position.x <= maxX + margin &&
        position.z >= minZ - margin && position.z <= maxZ + margin;

      // PRIORIDADE MÁXIMA: Verificar se a posição está completamente dentro da parcela
      // Isso impede que a nave entre dentro de qualquer parcel
      const isInsideParcel =
        position.x >= minX && position.x <= maxX &&
        position.y >= minY && position.y <= maxY &&
        position.z >= minZ && position.z <= maxZ;

      if (isInsideParcel) {
        if (barrier.id.includes('279')) {
          console.log('Nave DENTRO da parcel 279 detectada!');
        }
        animatedParcelTracker.setCurrentParcel(barrier.id);
        return true;
      }

      // PRIORIDADE 1: Se a nave está dentro dos limites horizontais, verificar colisão no TOPO
      if (isWithinHorizontalBounds) {
        // Verificar se a nave está próxima do topo da parcela
        // Usar o topo real da parcela (sem offset adicional)
        const distanceToTop = Math.abs(position.y - maxY);

        // Usar uma margem apropriada para detecção de colisão no topo
        // Ajustada para funcionar bem com todas as parcels (4x maior que antes)
        const topMargin = 0.8;

        // Se a nave está próxima do topo, considerar colisão no topo
        if (distanceToTop <= topMargin) {
          if (barrier.id.includes('279')) {
            console.log('Colisão no TOPO da parcel 279 detectada!');
          }
          animatedParcelTracker.setCurrentParcel(barrier.id);
          return true;
        }

        // Se a parcela estiver subindo e a nave estiver acima dela, não considerar colisão
        if (barrier.isAnimated && animatedParcelTracker.isCurrentParcelRising() && position.y > maxY) {
          // A nave está acima da parcela que está subindo, não há colisão
          continue;
        }

        // Se a nave está ACIMA do topo da parcela, NÃO verificar outras colisões
        if (position.y > maxY) {
          // Não há colisão se a nave está acima da parcela
          continue;
        }
      }

      // PRIORIDADE 2: Verificar colisões laterais apenas se a nave não estiver acima da parcela
      // Verificar se a posição está dentro ou muito próxima dos limites laterais da parcela
      const isNearLeft = Math.abs(position.x - minX) <= margin &&
                         position.z >= minZ - margin && position.z <= maxZ + margin &&
                         position.y >= minY - margin && position.y <= maxY + margin;

      const isNearRight = Math.abs(position.x - maxX) <= margin &&
                          position.z >= minZ - margin && position.z <= maxZ + margin &&
                          position.y >= minY - margin && position.y <= maxY + margin;

      const isNearFront = Math.abs(position.z - minZ) <= margin &&
                          position.x >= minX - margin && position.x <= maxX + margin &&
                          position.y >= minY - margin && position.y <= maxY + margin;

      const isNearBack = Math.abs(position.z - maxZ) <= margin &&
                         position.x >= minX - margin && position.x <= maxX + margin &&
                         position.y >= minY - margin && position.y <= maxY + margin;

      const isNearBottom = Math.abs(position.y - minY) <= margin &&
                           position.x >= minX - margin && position.x <= maxX + margin &&
                           position.z >= minZ - margin && position.z <= maxZ + margin;

      // Se a posição está próxima de qualquer face lateral da parcela, há colisão
      if (isNearLeft || isNearRight || isNearFront || isNearBack || isNearBottom) {
        if (barrier.id.includes('279')) {
          console.log('Colisão LATERAL com parcel 279 detectada!');
        }
        animatedParcelTracker.setCurrentParcel(barrier.id);
        return true;
      }
    }

    return false;
  }

  correctPosition(currentPosition: THREE.Vector3, previousPosition: THREE.Vector3): THREE.Vector3 {
    if (!this.checkCollision(currentPosition)) {
      return currentPosition;
    }

    // Encontrar a barreira mais próxima
    const closestBarrier = this.findClosestBarrier(currentPosition);

    if (!closestBarrier) {
      // Se não houver barreira próxima, retornar à posição anterior
      return previousPosition.clone();
    }

    // Para parcels problemáticas como a 279, usar sempre a posição anterior
    if (closestBarrier.id.includes('279')) {
      console.log('Colisão com parcel 279 - usando posição anterior para garantir');
      return previousPosition.clone();
    }

    // Obter a altura real da parcela
    let effectiveHeight = closestBarrier.size.height;
    if (closestBarrier.isAnimated) {
      const currentHeight = animatedParcelTracker.getCurrentParcelHeight();
      if (currentHeight !== null) {
        effectiveHeight = currentHeight - closestBarrier.position.y;
      }
    }

    // Calcular os limites exatos da parcela
    const minX = closestBarrier.position.x;
    const maxX = closestBarrier.position.x + closestBarrier.size.width;
    const minY = closestBarrier.position.y;
    const maxY = closestBarrier.position.y + effectiveHeight;
    const minZ = closestBarrier.position.z;
    const maxZ = closestBarrier.position.z + closestBarrier.size.depth;

    // Determinar qual face da barreira está mais próxima
    const faces = [
      { normal: new THREE.Vector3(-1, 0, 0), dist: Math.abs(currentPosition.x - minX), name: 'left' },
      { normal: new THREE.Vector3(1, 0, 0), dist: Math.abs(currentPosition.x - maxX), name: 'right' },
      { normal: new THREE.Vector3(0, -1, 0), dist: Math.abs(currentPosition.y - maxY), name: 'top' },
      { normal: new THREE.Vector3(0, 1, 0), dist: Math.abs(currentPosition.y - minY), name: 'bottom' },
      { normal: new THREE.Vector3(0, 0, -1), dist: Math.abs(currentPosition.z - minZ), name: 'front' },
      { normal: new THREE.Vector3(0, 0, 1), dist: Math.abs(currentPosition.z - maxZ), name: 'back' },
    ];

    // Encontrar a face mais próxima
    faces.sort((a, b) => a.dist - b.dist);
    const closestFace = faces[0];

    // Calcular o vetor de movimento para determinar a direção do desvio
    const movementVector = new THREE.Vector3().subVectors(currentPosition, previousPosition);

    // Aplicar correção de posição baseada na face mais próxima com um efeito de desvio
    const correctedPosition = currentPosition.clone();
    const safetyDistance = 0.2; // Distância de segurança para evitar colisões
    const slideFactor = 0.2; // Fator de deslizamento para o efeito de desvio

    // Aplicar correção baseada na face mais próxima e adicionar um componente de desvio
    switch (closestFace.name) {
      case 'left':
        correctedPosition.x = minX - safetyDistance;
        // Desviar na direção do movimento horizontal
        if (movementVector.z > 0) correctedPosition.z += slideFactor;
        else if (movementVector.z < 0) correctedPosition.z -= slideFactor;
        break;
      case 'right':
        correctedPosition.x = maxX + safetyDistance;
        if (movementVector.z > 0) correctedPosition.z += slideFactor;
        else if (movementVector.z < 0) correctedPosition.z -= slideFactor;
        break;
      case 'top':
        // Usar o topo real da parcela para a colisão
        correctedPosition.y = maxY + safetyDistance;
        // Desviar na direção do movimento horizontal
        if (movementVector.x > 0) correctedPosition.x += slideFactor;
        else if (movementVector.x < 0) correctedPosition.x -= slideFactor;
        if (movementVector.z > 0) correctedPosition.z += slideFactor;
        else if (movementVector.z < 0) correctedPosition.z -= slideFactor;
        break;
      case 'bottom':
        correctedPosition.y = minY - safetyDistance;
        if (movementVector.x > 0) correctedPosition.x += slideFactor;
        else if (movementVector.x < 0) correctedPosition.x -= slideFactor;
        break;
      case 'front':
        correctedPosition.z = minZ - safetyDistance;
        if (movementVector.x > 0) correctedPosition.x += slideFactor;
        else if (movementVector.x < 0) correctedPosition.x -= slideFactor;
        break;
      case 'back':
        correctedPosition.z = maxZ + safetyDistance;
        if (movementVector.x > 0) correctedPosition.x += slideFactor;
        else if (movementVector.x < 0) correctedPosition.x -= slideFactor;
        break;
    }

    // Verificar se a posição corrigida ainda está em colisão
    if (this.checkCollision(correctedPosition)) {
      // Se ainda estiver em colisão, retornar à posição anterior
      if (closestBarrier.id.includes('279')) {
        console.log('Posição corrigida ainda em colisão com parcel 279, usando posição anterior');
      }
      return previousPosition.clone();
    }

    // Verificar se a posição corrigida está muito próxima da parcel 279
    if (closestBarrier.id.includes('279')) {
      const distanceToParcel = Math.min(
        Math.abs(correctedPosition.x - minX),
        Math.abs(correctedPosition.x - maxX),
        Math.abs(correctedPosition.y - minY),
        Math.abs(correctedPosition.y - maxY),
        Math.abs(correctedPosition.z - minZ),
        Math.abs(correctedPosition.z - maxZ)
      );

      // Se estiver muito próxima, usar a posição anterior para garantir
      if (distanceToParcel < 0.3) {
        console.log('Posição corrigida muito próxima da parcel 279, usando posição anterior');
        return previousPosition.clone();
      }
    }

    return correctedPosition;
  }

  // Encontrar a barreira mais próxima de uma posição
  // Obter todas as barreiras para verificação externa
  getBarriers(): Barrier[] {
    return this.barriers;
  }

  // Adicionar ou atualizar uma barreira específica
  setBarrier(barrier: any): void {
    // Verificar se é uma configuração de castelo com múltiplas caixas de colisão
    if (barrier.collisionBoxes && Array.isArray(barrier.collisionBoxes)) {
      // Remover completamente os logs de configuração de barreiras

      // Remover barreiras existentes com o mesmo ID base
      this.barriers = this.barriers.filter(b => !b.id.startsWith(barrier.id));

      // Adicionar cada caixa de colisão como uma barreira separada
      barrier.collisionBoxes.forEach((box: any, index: number) => {
        const boxId = `${barrier.id}-part-${index}`;
        this.barriers.push({
          id: boxId,
          position: {
            x: box.position.x,
            y: box.position.y,
            z: box.position.z
          },
          size: {
            width: box.size.width,
            height: box.size.height,
            depth: box.size.depth
          },
          isAnimated: barrier.isAnimated
        });
      });
    } else {
      // Configuração de barreira padrão (única caixa)
      // Verificar se a barreira já existe
      const existingIndex = this.barriers.findIndex(b => b.id === barrier.id);

      if (existingIndex >= 0) {
        // Atualizar a barreira existente
        this.barriers[existingIndex] = barrier;
      } else {
        // Adicionar nova barreira
        this.barriers.push(barrier);
      }
    }

    // Recalcular os limites do mundo
    this.recalculateWorldBounds();
  }

  // Recalcular os limites do mundo com base em todas as barreiras
  private recalculateWorldBounds(): void {
    if (this.barriers.length === 0) return;

    // Inicializar com valores extremos
    this.worldBounds = {
      minX: Number.MAX_VALUE,
      maxX: Number.MIN_VALUE,
      minY: Number.MAX_VALUE,
      maxY: Number.MIN_VALUE,
      minZ: Number.MAX_VALUE,
      maxZ: Number.MIN_VALUE
    };

    // Atualizar com base em todas as barreiras
    for (const barrier of this.barriers) {
      this.worldBounds.minX = Math.min(this.worldBounds.minX, barrier.position.x);
      this.worldBounds.maxX = Math.max(this.worldBounds.maxX, barrier.position.x + barrier.size.width);
      this.worldBounds.minY = Math.min(this.worldBounds.minY, barrier.position.y);
      this.worldBounds.maxY = Math.max(this.worldBounds.maxY, barrier.position.y + barrier.size.height);
      this.worldBounds.minZ = Math.min(this.worldBounds.minZ, barrier.position.z);
      this.worldBounds.maxZ = Math.max(this.worldBounds.maxZ, barrier.position.z + barrier.size.depth);
    }
  }

  findClosestBarrier(position: THREE.Vector3): Barrier | null {
    if (this.barriers.length === 0) {
      return null;
    }

    let closestBarrier = this.barriers[0];
    let minDistance = this.getDistanceToBarrier(position, closestBarrier);

    for (let i = 1; i < this.barriers.length; i++) {
      const distance = this.getDistanceToBarrier(position, this.barriers[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestBarrier = this.barriers[i];
      }
    }

    return closestBarrier;
  }

  // Calcular a distância de uma posição a uma barreira
  private getDistanceToBarrier(position: THREE.Vector3, barrier: Barrier): number {
    // Calcular o centro da barreira
    const barrierCenter = new THREE.Vector3(
      barrier.position.x + barrier.size.width / 2,
      barrier.position.y + barrier.size.height / 2,
      barrier.position.z + barrier.size.depth / 2
    );

    // Calcular a distância ao centro da barreira
    return position.distanceTo(barrierCenter);
  }

  // Obter a distância à barreira mais próxima
  getDistanceToClosestBarrier(position: THREE.Vector3): number {
    if (this.barriers.length === 0) {
      return Infinity; // Se não houver barreiras, retornar distância infinita
    }

    // Encontrar a barreira mais próxima
    const closestBarrier = this.findClosestBarrier(position);
    if (!closestBarrier) {
      return Infinity;
    }

    // Calcular a distância à barreira mais próxima
    return this.getDistanceToBarrier(position, closestBarrier);
  }

  // Convert to the format expected by the existing collision system
  convertToMondrianBarriers(): any[] {
    if (this.barriers.length === 0) {
      return [];
    }

    return this.barriers.map(barrier => ({
      position: {
        x: barrier.position.x,
        y: barrier.position.z // In the existing system, y is used for the z-coordinate
      },
      size: Math.max(barrier.size.width, barrier.size.depth), // Use the larger dimension for collision
      height: barrier.size.height // Usar a altura visual exata para colisão
    }));
  }

  // Get world dimensions
  getWorldDimensions(): { width: number, height: number } {
    // Verificar se os limites do mundo foram inicializados
    if (this.worldBounds.maxX === 0 && this.worldBounds.minX === 0) {
      return { width: 0, height: 0 };
    }

    return {
      width: this.worldBounds.maxX - this.worldBounds.minX,
      height: this.worldBounds.maxZ - this.worldBounds.minZ
    };
  }
}

export const mmlCollisionSystem = new MMLCollisionSystem();
